import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { aggregator } from '@env/aggregator/aggregator';
import { MonitoringService } from '@core/services/monitoring.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(
    private msalAuthService: MsalService,
    private monitoringService: MonitoringService
  ) {   
  }

  public get isAuthenticated(): boolean{
    return this.getB2CAccounts().length > 0; 
  }

  public checkAndSetActiveAccount(): void {
    let activeAccount = this.msalAuthService.instance.getActiveAccount();
    let allAccounts = this.getB2CAccounts();

    // If there is an active account already and the accounts domain does not match the new domain set in config, clean the cache by logging out the user.
    if (activeAccount != null){
      if (activeAccount.environment != aggregator.settings.auth.domain){
        this.msalAuthService.instance.logoutRedirect();
      }
    }

    if (!activeAccount && allAccounts.length > 0) {
      this.msalAuthService.instance.setActiveAccount(allAccounts[0]);
    }
  }

  public getB2CAccounts() {
    return this.msalAuthService.instance.getAllAccounts().filter((account) => {
      return account?.environment == aggregator.settings.auth.domain
    });
  }

  /**
   * Removes only MSAL-related keys from localStorage, preserving user preferences.
   * This method selectively clears authentication tokens without affecting app-specific data.
   */
  public clearMsalStorage(): void {
    // Clear MSAL keys from localStorage
    const localKeysToRemove = this.identifyMsalKeys(localStorage);
    localKeysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear MSAL keys from sessionStorage
    const sessionKeysToRemove = this.identifyMsalKeys(sessionStorage);
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    this.monitoringService.logTrace(
      `Cleared ${localKeysToRemove.length} MSAL keys from localStorage and ${sessionKeysToRemove.length} keys from sessionStorage`
    );
  }

  /**
   * Identifies MSAL-related keys from a storage object
   */
  private identifyMsalKeys(storage: Storage): string[] {
    const msalPatterns = [
      'msal.',
      'server-telemetry-',
      '.onmicrosoft.com',
      '-accesstoken-',
      '-refreshtoken-',
      '-idtoken-'
    ];

    return Object.keys(storage).filter(key =>
      msalPatterns.some(pattern => key.includes(pattern))
    );
  }
}